import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { IMG } from "../data/services";
import { framework, industries, marqueeItems, processSteps } from "../data/site";
import { useCases } from "../lib/cases";
import { services } from "../data/services";
import { useSEO } from "../lib/seo";
import { FadeUp, Marquee, ParallaxImg } from "../components/motion";
import { CTABanner } from "../components/Footer";
import { CaseCard } from "../components/CaseCard";
import { useLang } from "../i18n";
import MotionHero from "../components/MotionHero";
import YuxelHomeServices from "../components/YuxelHomeServices";
import YuxelPartnerRail from "../components/YuxelPartnerRail";
import { useProjectCases } from "../lib/project-view";

const SectorCard = ({href, testid, children}) => href ? <Link to={href} data-testid={testid} className="group block">{children}</Link> : <div data-testid={testid} className="group block">{children}</div>;
const problems = [{"title":"Direction is unclear","description":"The business is growing, but priorities and decisions are fragmented.","index":0},{"title":"Brand value does not translate into price","description":"The business delivers quality, but the market does not recognise or reward it.","index":1},{"title":"Marketing is active, demand is inconsistent","description":"Campaigns run, but a predictable flow of enquiries does not develop.","index":2},{"title":"Enquiries arrive, sales do not close","description":"Leads are not converted into revenue by the process, offer and sales management.","index":3},{"title":"The offer does not stand out","description":"The product exists, but customers lack a clear reason to choose it.","index":5},{"title":"Decisions rely on intuition","description":"The contribution of channels, campaigns and teams is unclear.","index":6},{"title":"Processes depend too heavily on individuals","description":"Repetitive work consumes time and knowledge stays with individuals.","index":7}];
const ProblemSelector = () => { const {t,href}=useLang();return <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" aria-label="Problem selector"><p className="overline">{t("Where does your problem begin?")}</p><h2 className="mt-6 max-w-4xl">{t("The right service starts with naming the problem.")}</h2><div className="mt-12 grid gap-4 md:grid-cols-2">{problems.map((p,i)=><Link key={p.title} to={href('/services/'+services[p.index].slug)} className="problem-card"><span className="overline">0{i+1}</span><h3>{t(p.title)}</h3><p>{t(p.description)}</p><span className="mt-5 block font-semibold">{t(services[p.index].name)} ↗</span></Link>)}</div></section>;};
const Positioning = () => {
  const { t, href } = useLang();
  return (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32 lg:py-40" aria-label="What does YUXEL change?" data-tone="ivory">
    <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
      <FadeUp className="lg:col-span-3">
        <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-gold">
          <span className="text-white/40">01</span>
          <span className="h-px w-8 bg-gold/60" aria-hidden="true" />
          {t("What does YUXEL change?")}
        </p>
      </FadeUp>
      <div className="lg:col-span-9">
        <FadeUp>
          <h2>{t("The problem is often not marketing. It is the system marketing is disconnected from.")}</h2>
        </FadeUp>
        <FadeUp delay={0.15} className="mt-10"><p className="mt-6 max-w-2xl text-mist">{t("Advertising works, but sales do not follow. The brand is visible, but cannot justify premium pricing. The team is busy, but not working towards the same goal.")}</p><p className="mt-6 max-w-2xl text-mist">{t("More posts, another campaign or a new agency will not resolve this. The problem is that strategy, the offer, marketing and sales operate separately.")}</p><p className="mt-6 max-w-2xl text-mist">{t("YUXEL brings those parts into one commercial system. We reshape how the business makes decisions, creates demand and earns revenue.")}</p></FadeUp>
        <FadeUp delay={0.2} className="mt-10">
          <Link
            to={href("/about")}
            data-testid="positioning-about-link"
            className="group inline-flex items-center gap-2 text-sm font-semibold text-gold"
          >
            {t("Explore YUXEL’s role")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </FadeUp>
      </div>
    </div>
  </section>
  );
};

const Framework = () => {
  const {t} = useLang();
  const [active, setActive] = useState(0);
  return <section className="framework-section border-t border-white/10 bg-panel" aria-label="Transformation framework"><div className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
    <p className="overline">02 — {t("Seven levels of growth")}</p>
    <h2 className="mt-6 max-w-4xl">{t("Seven levels. One complete system for growth.")}</h2>
    <p className="mt-6 max-w-2xl text-mist">{t("Transformation is not a campaign. It is seven connected levels, built in the right sequence.")}</p>
    <div className="levels mt-14">{framework.map((f,i)=><div className={active===i ? "level active" : "level"} key={f.n} style={{"--level":i}}>
      <button aria-expanded={active===i} aria-controls={"level-"+f.n} onClick={()=>setActive(i)}><span>{f.n}</span><span>{t(f.t)}</span><span aria-hidden="true">{active===i ? "−" : "+"}</span></button>
      <p id={"level-"+f.n} hidden={active!==i}>{t(f.d)}</p>
    </div>)}</div>
  </div></section>;
};

const ServicesOverview = () => {
  const { t, href } = useLang();
  return (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32 lg:py-40" aria-label="Services">
    <FadeUp>
      <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
        <div>
          <p className="mb-5 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-gold">
            <span className="text-white/40">03</span>
            <span className="h-px w-8 bg-gold/60" aria-hidden="true" />
            {t("Services")}
          </p>
          <h2 className="max-w-2xl font-serif text-3xl font-medium leading-[1.15] text-white sm:text-4xl lg:text-5xl">
            {t("Eight systems working towards the same result.")}
          </h2><p className="mt-6 max-w-2xl text-mist">{t("Whatever brings you to YUXEL, we solve the problem in the context of the whole business.")}</p>
        </div>
        <Link
          to={href("/services")}
          data-testid="services-overview-all-link"
          className="shrink-0 text-sm font-semibold text-gold underline-offset-4 hover:underline"
        >
          {t("View all services")}
        </Link>
      </div>
    </FadeUp>
    <div className="mt-14 border-b border-white/10">
      {services.map((s, i) => (
        <FadeUp key={s.slug} delay={Math.min(i * 0.04, 0.2)}>
          <Link
            to={href(`/services/${s.slug}`)}
            data-testid={`home-service-row-${s.slug}`}
            className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 border-t border-white/10 px-2 py-6 transition-all duration-500 hover:bg-white/[0.02] hover:px-4 sm:gap-8 sm:py-8"
          >
            <span className="font-mono text-sm text-white/40">{s.index}</span>
            <div>
              <h3 className="font-display text-xl font-semibold text-white transition-colors duration-300 group-hover:text-gold sm:text-2xl">
                {t(s.name)}
              </h3>
              <p className="mt-1 text-sm text-mist">{t(s.short)}</p>
            </div>
            <ArrowUpRight className="h-5 w-5 text-white/30 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-gold" />
          </Link>
        </FadeUp>
      ))}
    </div>
  </section>
  );
};

const SelectedWork = () => {
  const { t, href } = useLang();
  const cases = useProjectCases();
  return (
  <section className="border-t border-white/10 bg-panel" aria-label="Selected work">
    <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32 lg:py-40">
      <FadeUp>
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <p className="mb-5 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-gold">
              <span className="text-white/40">04</span>
              <span className="h-px w-8 bg-gold/60" aria-hidden="true" />
              {t("Selected results")}
            </p>
            <h2 className="max-w-2xl font-serif text-3xl font-medium leading-[1.15] text-white sm:text-4xl lg:text-5xl">
              {t("The measurable side of transformation.")}
            </h2><p className="mt-6 max-w-2xl text-mist">{t("We present the problem solved, the system built and the commercial result achieved.")}</p>
          </div>
          <Link
            to={href("/work")}
            data-testid="home-work-all-link"
            className="shrink-0 text-sm font-semibold text-gold underline-offset-4 hover:underline"
          >
            {t("All projects")}
          </Link>
        </div>
      </FadeUp>
      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
        {cases.map((c, i) => (
          <FadeUp key={c.slug} delay={i * 0.08} className={i === 0 ? "md:col-span-2" : ""}>
            <CaseCard c={c} featured={i === 0} />
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
  );
};

const Industries = () => {
  const { t, href } = useLang();
  return (
  <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32 lg:py-40" aria-label="Industries">
    <FadeUp>
      <p className="mb-5 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-gold">
        <span className="text-white/40">05</span>
        <span className="h-px w-8 bg-gold/60" aria-hidden="true" />
        {t("Sector expertise")}
      </p>
      <h2 className="max-w-3xl font-serif text-3xl font-medium leading-[1.15] text-white sm:text-4xl lg:text-5xl">
        {t("Every sector speaks differently. Growth still needs a complete system.")}
      </h2><p className="mt-6 max-w-2xl text-mist">{t("We apply proven commercial experience in real estate to selected growth sectors through systems thinking.")}</p>
    </FadeUp>
    <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-8">
      {industries.map((ind, i) => (
        <FadeUp key={ind.name} delay={i * 0.1}>
          <SectorCard href={ind.href ? href(ind.href) : null} testid={`industry-card-${i}`}>
            <ParallaxImg
              src={ind.img}
              alt={t(ind.name)}
              speed={6}
              className="aspect-[4/5] border border-white/10"
            />
            <h3 className="mt-6 font-display text-xl font-semibold text-white transition-colors duration-300 group-hover:text-gold sm:text-2xl">
              {t(ind.name)}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-mist">{t(ind.d)}</p>
            {ind.href && <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
              {t("Explore the sector")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>}</SectorCard>
        </FadeUp>
      ))}
    </div>
  </section>
  );
};

const Process = () => {
  const { t, href } = useLang();
  return (
  <section className="border-t border-white/10" aria-label="Process">
    <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <FadeUp>
        <p className="mb-5 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-gold">
          <span className="text-white/40">06</span>
          <span className="h-px w-8 bg-gold/60" aria-hidden="true" />
          {t("Engagement model")}
        </p>
        <h2 className="max-w-3xl font-serif text-3xl font-medium leading-[1.15] text-white sm:text-4xl lg:text-5xl">
          {t("We start with the right diagnosis, not a ready-made package.")}
        </h2>
      </FadeUp>
      <div className="mt-14 grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-5">
        {processSteps.map((p, i) => (
          <FadeUp key={p.n} delay={i * 0.07}>
            <div data-testid={`process-step-${p.n}`} className="border-t border-white/15 pt-6">
              <div className="flex items-baseline justify-between">
                <span className="font-display text-3xl font-bold text-outline">{p.n}</span>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">
                  {t(p.w)}
                </span>
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-white">{t(p.t)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-mist">{t(p.d)}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
  );
};

export default function Home() {
  const { t, href, lang } = useLang();
  useSEO(
    t("YUXEL — Business Transformation, Marketing & Sales Systems"),
    t("YUXEL builds strategy, business architecture, brand, marketing, sales systems, analytics and AI solutions for medium and large businesses.")
  );
  return (
    <>
      <MotionHero />
      <YuxelHomeServices />
      <YuxelPartnerRail />
    </>
  );
}
